import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  IconButton,
  Tooltip
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { usePanelStyles } from '@/styles/utilities/hooks';
import MolstarViewer from '../../../shared/components/viewer/MolstarViewer';

/**
 * ReviewPanel Component
 * 
 * Displays a 3D molecular structure viewer for selected PDB files.
 * Can show structures from either a URL or a file object.
 * 
 * @param {Object} props
 * @param {boolean} props.isCollapsed - Whether the panel is collapsed
 * @param {Function} props.onCollapse - Callback to collapse the panel
 * @param {string} props.selectedPdbUrl - URL of the PDB file to display
 * @param {File} props.selectedFile - File object to display
 * @param {number} props.selectedStructureId - ID of the selected structure
 * @param {number} props.width - Panel width in pixels (density-aware)
 * @param {number} props.minWidth - Minimum panel width in pixels (density-aware)
 */
export default function ReviewPanel({ 
  isCollapsed,
  onCollapse,
  selectedPdbUrl,
  selectedFile,
  selectedStructureId,
  width,
  minWidth
}) {
  const theme = useTheme();
  const panelStyles = usePanelStyles();
  
  // Use same width as left sidebar panels for consistency
  const panelWidth = width || theme.dimensions.panel.leftSidebarWidth;
  const panelMinWidth = minWidth || theme.dimensions.panel.leftSidebarMinWidth;

  if (isCollapsed) {
    return null;
  }

  return (
    <Paper sx={{ 
      width: panelWidth,
      minWidth: panelMinWidth,
      display: 'flex', 
      flexDirection: 'column',
      borderRadius: 0,
      borderLeft: '1px solid',
      borderColor: 'divider'
    }}>
      {/* Header */}
      <Box sx={{ 
        p: theme.spacing(1.5), 
        height: theme.dimensions.panel.headerHeight,
        borderBottom: '1px solid', 
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: theme.palette.grey[50]
      }}>
        <Typography sx={panelStyles.headerTitle}>
          Review {selectedStructureId ? `#${selectedStructureId}` : ''}
        </Typography>
        <Tooltip title="Collapse panel">
          <IconButton 
            size="small" 
            onClick={onCollapse}
            sx={{ padding: theme.spacing(0.5) }}
          >
            <ChevronRight sx={{ fontSize: theme.dimensions.icon.small }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {(selectedPdbUrl || selectedFile) ? (
          <MolstarViewer 
            pdbUrl={selectedPdbUrl} 
            pdbFile={selectedFile}
          />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            p: theme.spacing(2)
          }}>
            <Typography color="text.secondary" align="center" sx={{ fontSize: theme.typography.fontSize.base }}>
              Select a structure from the table to view it in 3D
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}